import { ErrorHandler, Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import { from } from 'rxjs';
import { sessionStorageVersions } from '../collection-versions';
import type {
  DatastoreCollectionType,
  UserCollectionStateSlice,
} from '../store.model';
import { LocalCacheStorage } from './local-cache-storage.service';
import type { CollectionSliceCache } from './local-cache.interface';
import {
  COLLECTION_CACHE_INTERFACE_VERSION,
  isCollectionSliceCache,
} from './local-cache.interface';

// 5e543256c480ac577d30f76f9120eb74 === md5("undefined")
const UNDEFINED_MARKER = '5e543256c480ac577d30f76f9120eb74#undefined';

function generateStorageKey(collectionType: string): string {
  return `datastore_local_cache_${collectionType}`;
}

/**
 * Merge the given documents and queries into the current user collection state slice by:
 *   - adding missing documents and queries if it is not already exist
 *   - updating existing documents or queries if a newer version is available
 *
 * @param currentUserCollectionSlice The current user collection state slice object.
 * @param docsAndQueriesToAdd Documents and queries which we would like to merge into the current slice.
 */
export function mergeUserCollectionStateSlice<
  C extends DatastoreCollectionType,
>(
  currentUserCollectionSlice: UserCollectionStateSlice<C> = {
    queries: {},
    documents: {},
  },
  docsAndQueriesToAdd: UserCollectionStateSlice<C> = {
    queries: {},
    documents: {},
  },
): UserCollectionStateSlice<C> {
  return {
    queries: {
      ...currentUserCollectionSlice.queries,
      ...Object.fromEntries(
        Object.entries(docsAndQueriesToAdd.queries).filter(
          ([queryString, query]) => {
            const queryInCollection =
              currentUserCollectionSlice.queries[queryString];
            return (
              // Does not currently exist.
              !queryInCollection ||
              // This query result is older than the current query result.
              query.timeUpdated >= queryInCollection.timeUpdated
            );
          },
        ),
      ),
    },
    documents: {
      ...currentUserCollectionSlice.documents,
      ...Object.fromEntries(
        Object.entries(docsAndQueriesToAdd.documents).filter(([id, doc]) => {
          const docInCollection = currentUserCollectionSlice.documents[id];
          return (
            // Does not currently exist.
            !docInCollection ||
            // This document is older than the one currently in the collection.
            doc.timeUpdated >= docInCollection.timeUpdated
          );
        }),
      ),
    },
  };
}

/**
 * Serialise a collection slice cache object into a JSON string for storage.
 *
 * `JSON.stringify` rewrites `undefined` array elements to `null`.
 * To preserve them, `undefined` elements are replaced with UNDEFINED_MARKER,
 * but only inside arrays — `undefined` object properties are left to be
 * dropped as usual.
 *
 * @param cacheObj The collection slice cache object to serialise.
 */
export function serializeCollectionSliceCache<
  C extends DatastoreCollectionType,
>(cacheObj: CollectionSliceCache<C>): string {
  return JSON.stringify(cacheObj, function replacer(_, value) {
    // Only replace UNDEFINED_MARKER if it is an array element
    if (!Array.isArray(this)) {
      return value;
    }

    return value === undefined ? UNDEFINED_MARKER : value;
  });
}

/**
 * Recursively walk a parsed object and replace every UNDEFINED_MARKER array
 * element with a real `undefined`, mutating the object in place.
 *
 * This is done as a post-parse pass rather than via a `JSON.parse` reviver on
 * purpose. A reviver that returns `undefined` for an array element does not
 * set the slot to `undefined` — it *deletes* it, leaving a sparse hole. A hole
 * and an `undefined` value are not equivalent: `index in array` is `false` for
 * a hole but `true` for `undefined`, and iteration/length behave differently.
 * Assigning `obj[index] = undefined` here produces a genuine `undefined` slot,
 * which is what round-trips the original value faithfully.
 *
 * @param obj The parsed value to walk and mutate in place.
 */
function reviveUndefinedArrayElement(obj: any): any {
  if (Array.isArray(obj)) {
    obj.forEach((value, index) => {
      if (value === UNDEFINED_MARKER) {
        obj[index] = undefined;
      } else {
        reviveUndefinedArrayElement(value);
      }
    });
  } else if (obj && typeof obj === 'object') {
    for (const key of Object.keys(obj)) {
      // Don't revive UNDEFINED_MARKER in object property,
      // just passthrough the values in the object
      reviveUndefinedArrayElement(obj[key]);
    }
  }

  return obj;
}

/**
 * Deserialise a JSON string from session storage back into a collection slice
 * cache object.
 *
 * Reverses the serialisation by reviving UNDEFINED_MARKER array elements back
 * to `undefined`. Returns `undefined` if the parsed value is not a valid
 * collection slice cache.
 *
 * @param sessionStorageValue The JSON string previously stored in session storage.
 */
export function deserializeCollectionSliceCache<
  C extends DatastoreCollectionType,
>(sessionStorageValue: any): CollectionSliceCache<C> | undefined {
  const cachedCollectionState = reviveUndefinedArrayElement(
    JSON.parse(sessionStorageValue),
  );

  return isCollectionSliceCache<C>(cachedCollectionState)
    ? cachedCollectionState
    : undefined;
}

@Injectable({
  providedIn: 'root',
})
export class LocalCacheHelpers {
  private storage = inject(LocalCacheStorage);
  private errorHandler = inject(ErrorHandler);

  private readonly PARSED_CACHE_TTL = 10_000;

  // Last parsed value per collection, reused while the stored string is unchanged
  private parsedCaches = new Map<
    string,
    {
      readonly value: string;
      readonly parsed: CollectionSliceCache<any> | undefined;
      readonly time: number;
    }
  >();

  /**
   * Get the cached collection state slice from the session storage
   * for the given collection type.
   *
   * This returns an observable so we can test it with code that uses
   * marble diagrams. The issue appears to be with having virtual times
   * of marble diagrams with the definitely not-virtual-times of promises.
   * See the issues from a similar rx-marble library for more explanation:
   * - https://github.com/cartant/rxjs-marbles/issues/11
   * - https://github.com/cartant/rxjs-marbles/issues/71
   */
  getDatastoreCacheByCollectionType<C extends DatastoreCollectionType>(
    collectionType: string,
  ): Observable<CollectionSliceCache<C> | undefined> {
    return from(
      this.storage
        .get(generateStorageKey(collectionType))
        .catch(err => undefined) // Ignore errors in storage.get
        .then(sessionStorageValue => {
          if (!sessionStorageValue) {
            return undefined;
          }

          const now = Date.now();
          for (const [key, entry] of this.parsedCaches) {
            if (now - entry.time > this.PARSED_CACHE_TTL) {
              this.parsedCaches.delete(key);
            }
          }

          const parsedCache = this.parsedCaches.get(collectionType);
          if (
            parsedCache !== undefined &&
            parsedCache.value === sessionStorageValue
          ) {
            return parsedCache.parsed as CollectionSliceCache<C> | undefined;
          }

          try {
            const parsed =
              deserializeCollectionSliceCache<C>(sessionStorageValue);
            this.parsedCaches.set(collectionType, {
              value: sessionStorageValue,
              parsed,
              time: now,
            });
            return parsed;
          } catch (err: any) {
            this.errorHandler.handleError(
              new JsonParseError(sessionStorageValue, err),
            );
          }
        }),
    );
  }

  /**
   * Store the given cache object into session storage by collection type.
   */
  setDatastoreCacheByCollectionType<C extends DatastoreCollectionType>(
    collectionType: string,
    cacheObj: CollectionSliceCache<C>,
  ): Observable<void> {
    return from(
      this.storage
        .set(
          generateStorageKey(collectionType),
          serializeCollectionSliceCache(cacheObj),
        )
        .catch(err => {
          // Ignore the errors, e.g. quota is full or security error.
        }),
    );
  }

  getJsonSchemaHash(collectionType: string): string {
    return sessionStorageVersions[collectionType];
  }

  getWebappBuildTimestamp(): number | undefined {
    return window?.webapp?.version?.buildTimestamp;
  }

  getCurrentCollectionCacheVersion(): number {
    return COLLECTION_CACHE_INTERFACE_VERSION;
  }
}

class JsonParseError extends Error {
  value: string;

  constructor(value: string, originalError: Error) {
    super(originalError.message);
    this.value = value;
  }
}
